import { isEditorActive, RichText, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import React, { FC, useMemo, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { iconSize } from '../../constants/icon.constants';
import Heading from '../@shared/heading';
import Icon from '../@shared/icon';
import { ContentHubCompatibleLoadersModel } from './models/contentHubCompatibleLoaders.model';
import { TCHCompatibleLoadersProps } from './types/contentHubCompatibleLoaders.types';
import RoutableNotEditableLink from '../@shared/routableNotEditableLink';
import { appendToUrlPath } from '../../utils/link.utils';
import Picture from '../@shared/picture';
import { windowService, documentService } from '../../utils/window.utils';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import useExperienceEditorPlaceholder from '../../hooks/useExperienceEditorPlaceholder';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import { appendRootToRichTextInternalLinks } from '../../utils/appendRootToRichTextInternalLinks.utils';
import './styles.scss';

const ProductCompatibleLoaderListing: FC<TCHCompatibleLoadersProps & WithTranslation> = (props) => {
    const model = useMemo(() => new ContentHubCompatibleLoadersModel(props), [props]);
  const { t } = props;
  const apiResponse = isEmptyResponse(props);
  const apiDataLength = props?.fields?.apiData?.items?.length > 0;
  const checkEmptiness = apiResponse && apiDataLength;
  const [showMoreLoaders, setShowMoreLoader] = useState(false);
  const isPlaceholderShown = useExperienceEditorPlaceholder(props);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!checkEmptiness && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }

  // No data and not in EE
  if (!checkEmptiness && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }
 
  const modelCards = (loader) => {
    // checking for skip series
    const url = loader?.lineUrl === loader?.titleUrl ? loader.lineUrl : `${loader?.lineUrl}/${loader?.titleUrl}`;
    const equipmentDescription = appendRootToRichTextInternalLinks(loader?.shortDescription);
          return (
        <RoutableNotEditableLink
          key={loader?.id}
          href={appendToUrlPath(model.baseUrl || '', url || '')}
          onClick={(e) => !url && e.preventDefault()}
          className={`ch-compatible-equipment__link ${!url ? 'ch-compatible-equipment__link--none' : ''} `}
        >
            <Picture
          productImageProps={{
            desktopRendition: loader?.assets?.[0]?.rendition_desktop_size500,
            mobileRendition: loader?.assets?.[0]?.rendition_mobile_size500,
            alt: loader?.assets?.[0]?.alt,
            pictureClassName: 'ch-compatible-equipment__card-image', 
            imageClassName: 'ch-compatible-equipment__card-image-wrapper', 
          }}
        />
          <h4 className={'ch-compatible-equipment__card-title'}>{loader?.title}</h4>
          <RichText tag={'div'} className={'ch-compatible-equipment__card-text rich-text'} field={{ value: equipmentDescription }} />
        </RoutableNotEditableLink>
      );
      };
  
  const showMoreLoaderEvent = () => {
    setShowMoreLoader(!showMoreLoaders);
    if (showMoreLoaders) {
      const element = documentService().getElementById(props.id);
      const headerOffset = 85;
      const elementPosition = element?.getBoundingClientRect().top;
      const offsetPosition = elementPosition ? elementPosition + windowService().pageYOffset - headerOffset : 0;

      windowService().scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <article className={'ch-compatible-equipment'} id={props.id}>
        {(model.title?.value || model.description?.value) && (
          <header className={'ch-compatible-equipment__header'}>
            <Heading title={model.title} subtitle={model.description} editable={true} />
          </header>
        )}
        <div className={'ch-compatible-equipment__content-container'}>
          <section className={'ch-compatible-equipment__content'}>
            {model.items?.filter((_, index) => (!showMoreLoaders && index < 4) || showMoreLoaders).map(modelCards)}
          </section>
          {model.items?.length > 4 && (
            <section className={'ch-compatible-equipment__show-more-section'}>
              <button className={'ch-compatible-equipment__show-more'} onClick={showMoreLoaderEvent}>
                <span>{!showMoreLoaders ? t('cnhi-showMore') : t('cnhi-showLess')}</span>
                <Icon iconName={!showMoreLoaders ? 'arrow-chevron-down' : 'arrow-chevron-up'} size={iconSize.dsk} color="primary" />
              </button>
            </section>
          )}
        </div>
      </article>
    </>
  );
};

export default withSitecoreContext()(withTranslation()(ProductCompatibleLoaderListing));
